import { useEffect } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import { PageError } from '@crehana/compass.ui/page-error';
import { useFeatureFlag } from '@crehana/feature-flags/react';
import type { TAppProps } from '@crehana/next';
import { AuthDialogProvider, LightMenu, PrivatePage } from '@crehana/web';

import { useCourseRestriction } from '@/shared/hooks/use-course-restriction';

import { CourseNotFoundView } from './components/course-not-found-view';
import ErrorView from './components/error-view';
import SEOStructuredData from './components/SEOStructuredData';
import {
  COURSE_DETAIL_AMPLITUDE_PAGE_NAME,
  LEARNING_COURSE_DETAIL_FEATURE_FLAGS,
} from './constants';
import CourseProvider from './context/CourseProvider';
import { withProviders } from './hoc/with-providers';
import { usePageView } from './hooks';
import useKnowledgeHubCourse from './hooks/use-knowledge-hub-course';
import { useKnowledgeHubCourseV2 } from './hooks/use-knowledge-hub-course-v2';
import SEO from './SEO';
import Main from './sections/Main';
import type { TCourseDetailProps } from './types';

const App: React.FC<TAppProps<TCourseDetailProps>> = ({
  countryCode,
  languageCode,
  contentSlug,
  organizationId,
  organizationSlug,
}) => {
  const { logEvent } = useAmplitude();
  const contentId = contentSlug?.split('-')[0];

  const {
    flagValue: isKnowledgeHubCollaboratorV2Enabled,
    loading: isLoadingCollaboratorV2Flag,
  } = useFeatureFlag(
    LEARNING_COURSE_DETAIL_FEATURE_FLAGS.LEARNING_KNOWLEDGEHUB_COLLABORATOR_V2,
  );

  const {
    course: courseDetailV1,
    loading: isLoadingKnowledgeHubCourse,
    error: errorKnowledgeHubCourse,
  } = useKnowledgeHubCourse({
    variables: {
      organizationId: String(organizationId),
      contentId: contentId ? Number(contentId) : undefined,
      contentSlug: contentId ? undefined : contentSlug,
    },
    skip: isLoadingCollaboratorV2Flag || isKnowledgeHubCollaboratorV2Enabled,
  });

  const {
    course: courseDetailV2,
    loading: isLoadingKnowledgeHubCourseV2,
    error: errorKnowledgeHubCourseV2,
  } = useKnowledgeHubCourseV2({
    organizationId: String(organizationId),
    contentId: contentId ? Number(contentId) : 0,
    skip:
      isLoadingCollaboratorV2Flag ||
      !isKnowledgeHubCollaboratorV2Enabled ||
      !contentId,
  });

  const activeCourseLoading = isKnowledgeHubCollaboratorV2Enabled
    ? isLoadingKnowledgeHubCourseV2
    : isLoadingKnowledgeHubCourse;

  const activeCourseError = isKnowledgeHubCollaboratorV2Enabled
    ? errorKnowledgeHubCourseV2
    : errorKnowledgeHubCourse;

  const course = isKnowledgeHubCollaboratorV2Enabled
    ? courseDetailV2
    : courseDetailV1;

  const courseId = course?.originalId;

  const {
    flagValue: isCourseRestrictionEnabled,
    loading: isLoadingCourseRestrictionEnabled,
  } = useFeatureFlag(
    LEARNING_COURSE_DETAIL_FEATURE_FLAGS.LEARNING_COURSE_RESTRICTION_RULES,
  );

  const {
    courseRestricted,
    isError: isErrorCourseRestriction,
    isLoading: isLoadingCourseRestriction,
  } = useCourseRestriction({
    courseId: courseId,
    organizationId: organizationId,
    skip: !courseId || !isCourseRestrictionEnabled,
  });

  const isLoading =
    isLoadingCollaboratorV2Flag ||
    activeCourseLoading ||
    isLoadingCourseRestriction ||
    isLoadingCourseRestrictionEnabled;

  usePageView({
    type: 'course',
    id: Number(courseId) || Number.NaN,
    pageData: {
      data: {},
      country_code: countryCode ?? undefined,
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useEffect(() => {
    if (courseId && course?.title) {
      logEvent({
        event: 'Render Org Course Detail',
        data: {
          ...getSourceEventParams(),
          'Course ID': courseId,
          'Course Name': course.title,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const renderContent = () => {
    if (activeCourseError || isErrorCourseRestriction) {
      return <ErrorView onReload={() => window.location.reload()} />;
    }

    if (courseRestricted) {
      return (
        <PageError
          amplitudePageName={COURSE_DETAIL_AMPLITUDE_PAGE_NAME}
          organizationSlug={organizationSlug}
          errorCode={403}
        />
      );
    }

    if (!course) {
      return <CourseNotFoundView onBack={() => window.history.back()} />;
    }

    return (
      <>
        <SEO course={course} />

        <LightMenu
          layoutConfig={{
            hideLoginButton: true,
          }}
          amplitudePageName={COURSE_DETAIL_AMPLITUDE_PAGE_NAME}
          countryPrefix={languageCode}
        />

        <AuthDialogProvider>
          <CourseProvider course={course} organizationSlug={organizationSlug}>
            <Main
              courseDescription={course.description}
              courseDifficulty={course.level?.name}
            />
          </CourseProvider>
        </AuthDialogProvider>

        <SEOStructuredData course={course} />
      </>
    );
  };

  return (
    <PrivatePage loading={isLoading} languageCode={languageCode}>
      {renderContent()}
    </PrivatePage>
  );
};

export default withProviders(App);
